import * as React from "react";
import { Loader2 } from "lucide-react";

import {
  BROKER_ALERT_TITLE_CLASS,
  BROKER_FIELD_DESC_CLASS,
} from "@/components/Broker/brokerAlertUi";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Field, FieldDescription, FieldLabel } from "@/components/ui/field";
import { cn } from "@/lib/utils";
import { accountSettingsFieldClassName } from "@/views/Dashboard/Profile/components/SettingCard";
import { MARKET_DATA_RESOLVE_DIALOG_COPY } from "@/constants/marketDataResolveDialogCopy";

export function MarketDataResolveDialogShell(props: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  title: React.ReactNode;
  description?: React.ReactNode;
  children: React.ReactNode;
  footer?: React.ReactNode;
  size?: "md" | "lg";
  "data-testid"?: string;
}): React.ReactElement {
  return (
    <Dialog open={props.open} onOpenChange={props.onOpenChange}>
      <DialogContent
        data-market-data-resolve-dialog=""
        data-testid={props["data-testid"]}
        className={cn(
          "flex max-h-[90vh] flex-col gap-0 overflow-hidden p-0 font-[family-name:var(--font-primary)]",
          props.size === "lg" ? "sm:max-w-3xl" : "sm:max-w-lg"
        )}
        onOpenAutoFocus={(event) => event.preventDefault()}
        onCloseAutoFocus={(event) => event.preventDefault()}
      >
        <DialogHeader className="shrink-0 space-y-2 border-b px-6 py-4 pr-14">
          <DialogTitle className="text-[color:var(--brand-tropical-surf)]">
            {props.title}
          </DialogTitle>
          {props.description ? (
            <DialogDescription className={cn(BROKER_FIELD_DESC_CLASS, "m-0")}>
              {props.description}
            </DialogDescription>
          ) : null}
        </DialogHeader>
        <div className="min-h-0 flex-1 overflow-y-auto overflow-x-hidden px-6 py-4">
          {props.children}
        </div>
        {props.footer}
      </DialogContent>
    </Dialog>
  );
}

export function MarketDataResolveDialogField(props: {
  label: string;
  description?: string;
  htmlFor?: string;
  children: React.ReactNode;
  className?: string;
}): React.ReactElement {
  return (
    <Field className={cn(accountSettingsFieldClassName, "w-full max-w-md", props.className)}>
      <FieldLabel htmlFor={props.htmlFor} className={BROKER_ALERT_TITLE_CLASS}>
        {props.label}
      </FieldLabel>
      {props.description ? (
        <FieldDescription className={BROKER_FIELD_DESC_CLASS}>
          {props.description}
        </FieldDescription>
      ) : null}
      {props.children}
    </Field>
  );
}

export function MarketDataResolveDialogFooter(props: {
  onCancel: () => void;
  onConfirm: () => void;
  confirmLabel?: string;
  confirmDisabled?: boolean;
  loading?: boolean;
  cancelLabel?: string;
}): React.ReactElement {
  return (
    <DialogFooter className="shrink-0 gap-2 border-t px-6 py-4 sm:justify-end">
      <Button
        type="button"
        variant="outline-gray"
        size="sm"
        data-font="primary"
        className="h-8 px-5 text-[0.75rem] normal-case tracking-normal"
        onClick={props.onCancel}
      >
        {props.cancelLabel ?? MARKET_DATA_RESOLVE_DIALOG_COPY.cancel}
      </Button>
      <Button
        type="button"
        variant="gradient"
        size="sm"
        data-font="primary"
        className="h-8 px-5 text-[0.75rem] normal-case tracking-normal"
        disabled={props.confirmDisabled || props.loading}
        onClick={props.onConfirm}
      >
        {props.loading ? (
          <>
            <Loader2 className="size-3.5 animate-spin" aria-hidden />
            Saving…
          </>
        ) : (
          props.confirmLabel ?? MARKET_DATA_RESOLVE_DIALOG_COPY.confirmMarketDataSource
        )}
      </Button>
    </DialogFooter>
  );
}
